/**
 * App stage for feature flags.
 *
 * Derived from the host the app is served from, because every deployed
 * environment is a Vite production build — `import.meta.env.PROD` is true on
 * app-test just as it is on thecreatorverse.co and cannot distinguish them.
 * Only the two production hostnames count as `prod`; anything else, including
 * unrecognised hosts, is treated as non-production so internal environments
 * keep full access.
 *
 * `VITE_STAGE` overrides the host check when set, mirroring backend `STAGE`.
 */

const PROD_HOSTS = new Set(["thecreatorverse.co", "www.thecreatorverse.co"]);

const NON_PROD_HOSTS = new Map<string, string>([
	["app-test.thecreatorverse.co", "staging"],
	["demo.thecreatorverse.co", "staging"],
]);

/** Pure stage resolution, split out so it can be tested without a DOM. */
export const resolveStage = (hostname?: string, explicitStage?: string): string => {
	const explicit = explicitStage?.trim().toLowerCase();
	if (explicit) return explicit;

	if (!hostname) return "dev";

	const host = hostname.toLowerCase();
	if (PROD_HOSTS.has(host)) return "prod";

	return NON_PROD_HOSTS.get(host) ?? "dev";
};

// The host cannot change without a full page load, so resolve on first use and
// reuse the result — these flags are read during render.
let cachedStage: string | undefined;

export const getAppStage = (): string => {
	cachedStage ??= resolveStage(
		typeof window === "undefined" ? undefined : window.location.hostname,
		import.meta.env.VITE_STAGE,
	);
	return cachedStage;
};

/** True when running against the production stage (`STAGE=prod`). */
export const isProdStage = (): boolean => getAppStage() === "prod";
